import { z } from "zod";
import type {
  RecipientRelationship,
  SchrijvenTaskType,
  SchrijvenVariantContext,
} from "../domain/schrijven";

// ---------------------------------------------------------------------------
// Response schema — the JSON the model must emit
// ---------------------------------------------------------------------------

export const SCHRIJVEN_RESPONSE_SCHEMA = z.object({
  execution: z.number().int().min(0).max(3),
  grammar: z.number().int().min(0).max(2),
  spelling: z.number().int().min(0).max(2),
  clearness: z.number().int().min(0).max(1),
  vocabulary: z.number().int().min(0).max(2),
  tutorParagraph: z.string().min(1),
  tutorParagraphTranslation: z.string().nullable(),
});

export type SchrijvenResponseJson = z.infer<typeof SCHRIJVEN_RESPONSE_SCHEMA>;

// ---------------------------------------------------------------------------
// Task-type specific rubric hints (injected into the system prompt)
// ---------------------------------------------------------------------------

const TASK_TYPE_HINTS: Record<SchrijvenTaskType, string> = {
  email: `This is an email (e-mail) task. Register depends on the recipient
relationship (see "Register check" below). Greeting (Aanhef) and closing
(Afsluiting) must match the variant's greeting and closing fields if
non-null. Check that all required bullets appear somewhere in the body.`,

  wijkkrant: `This is a neighbourhood newsletter (wijkkrant) contribution.
Register is public and semi-formal. No personal greeting is required. The
text should read as an announcement or short article.

HARD CONSTRAINT: wijkkrant must be at least 3 sentences. A "sentence" ends
with . ? or !. If the student wrote fewer than 3 sentences, execution
cannot exceed 1 even if all bullets are touched on.

Check that all required bullets appear somewhere in the text.`,

  form: `This is a form-filling task. The writer fills in labelled fields.
Evaluate whether all requested fields (bullets) are completed with
plausible content. A field left blank where the prompt mandates an answer
is a missed bullet for adequaatheid. Grammar applies per field, not across
a continuous text.`,

  briefje: `This is a short note (briefje) — typically left for a
neighbour, colleague or family member. Register is informal but still
correct Dutch (see "Register check" below — briefje is almost always
je-form). A greeting and closing are optional but common. Check that all
required bullets appear somewhere in the note.`,
};

const REGISTER_RULES: Record<RecipientRelationship, string> = {
  authority: `Recipient is an authority figure or institution (huisarts,
gemeente, school, makelaar, employer). Expect u-form throughout (u, uw,
uzelf). Any je / jij / jouw / jullie is a register slip and must be
flagged in tutorParagraph. The student should not use first names unless
the variant's recipient is a named person with a title (e.g. "Mevrouw
Bakker").`,

  peer: `Recipient is a peer (neighbour, colleague, classmate, friend).
Either u-form OR je-form is acceptable but must be CONSISTENT — switching
between the two within one message is a register error. Briefje almost
always uses je-form; email to a colleague is borderline and either is
fine.`,

  family: `Recipient is family (oma, opa, ouder, kind, partner). Expect
je-form (je, jij, jouw, jullie) and an intimate greeting/closing (Lieve…,
Liefs,). Sudden u-form is a register error.`,
};

// ---------------------------------------------------------------------------
// System prompt builder
// ---------------------------------------------------------------------------

export function buildSchrijvenSystemPrompt(taskType: SchrijvenTaskType): string {
  const hint = TASK_TYPE_HINTS[taskType];
  return `You are a strict but supportive Dutch A2 writing examiner grading a DUO-style ${taskType} task.

## Task type context
${hint}

## Scoring rubric (return integers)

- execution (0-3) — adequaatheid / bullet coverage.
  - 0: any *mandatory* bullet is not addressed at all, OR the answer is
       blank, OR the response is off-topic. Per DUO scoring this is an
       auto-zero gate — see "Gate rule" below.
  - 1: at least one bullet is missing or thinly touched.
  - 2: all bullets touched on but some are thin or under-developed.
  - 3: all bullets addressed clearly and with appropriate detail.

- grammar (0-2) — verb conjugation, word order (especially V2), articles
  (de/het), plurals, prepositions at A2 level.
  - 0: many A2-level errors that obscure meaning.
  - 1: some errors but meaning is clear.
  - 2: mostly correct A2 grammar.

- spelling (0-2) — correct spelling. Personal details (names, numbers,
  addresses) must be EXACTLY as given by the variant; if the variant
  specifies a name or number the student must reproduce it character-for-
  character. Off-by-one digit in a phone number is a spelling fault.
  - 0: frequent errors.
  - 1: noticeable errors but mostly readable.
  - 2: clean spelling.

- clearness (0-1) — begrijpelijkheid. Logical connectives used at least
  once (en, maar, want, omdat, dus, toch, of, daarom, voordat, terwijl).
  Order of ideas is coherent. This is a hard 0 if the response is
  incomprehensible or has zero connectors AND zero logical flow — and
  per DUO scoring a 0 here ALSO cascades (see "Gate rule").
  - 0: incoherent or no connector words used at all.
  - 1: at least one connector used appropriately and ideas flow.

- vocabulary (0-2) — A2-appropriate word choice.
  - 0: mostly L1 / wrong words / repetitive.
  - 1: adequate but limited.
  - 2: varied and correct.

## Gate rule (DUO auto-zero behaviour)

If EITHER of these is true, set grammar, spelling, and vocabulary all to 0
regardless of what was actually written:

1. execution = 0
2. clearness = 0

(execution and clearness themselves keep their computed score; only the
other three sub-scores cascade to 0.)

## Register check

When the variant specifies a recipientRelationship, enforce the rule below.
Mismatched register is at minimum a tutorParagraph callout and at most a
grammar-score deduction. ${
    // The variant prompt below carries the actual recipientRelationship
    // value; the rules table is reproduced here so the model has it
    // in-context.
    "Rules:"
  }

- authority: ${REGISTER_RULES.authority}
- peer:      ${REGISTER_RULES.peer}
- family:    ${REGISTER_RULES.family}

If the variant has no recipientRelationship (form, wijkkrant), skip this
check entirely.

## Invent-X check

If the variant carries an inventCount, the student must invent exactly
that many concrete items of their own (e.g. three things they want to
mention in the note). Count how many distinct items the student actually
included.
- All N present  → no penalty.
- N-1 present    → minus 1 execution point.
- Fewer than N-1 → execution = 0 (auto-zero gate fires).

## Output format

Reply ONLY with valid JSON matching this schema — no markdown, no prose
outside the JSON, no explanation lines:

{
  "execution":  <int 0-3>,
  "grammar":    <int 0-2>,
  "spelling":   <int 0-2>,
  "clearness":  <int 0-1>,
  "vocabulary": <int 0-2>,
  "tutorParagraph": "<2-4 sentences in Dutch, u-form, pointing out the strongest point then the single most important improvement area. Mention any register mismatch or invent-X miss explicitly.>",
  "tutorParagraphTranslation": "<translation of tutorParagraph into the selected language, or null if selected language is nl-NL>"
}

## Translation field

The user prompt will include a "Selected language" line carrying a BCP-47
locale code. If that code is "nl-NL", set tutorParagraphTranslation to
null. Otherwise, translate tutorParagraph into the selected language
faithfully:
- Preserve the calm, supportive register.
- Do not invent new feedback.
- Do not include language-tag prefixes or markdown.
- A user reading only the translation should understand exactly what the
  Dutch paragraph said.`;
}

// ---------------------------------------------------------------------------
// User prompt builder
// ---------------------------------------------------------------------------

export function buildSchrijvenUserPrompt(
  userText: string,
  variant: SchrijvenVariantContext,
  selectedLanguageBcp47: string,
): string {
  const bulletList = variant.bullets.map((b, i) => `  ${i + 1}. ${b}`).join("\n");
  const parts: string[] = [
    `Selected language: ${selectedLanguageBcp47}`,
    `## Scenario\n${variant.scenario}`,
    `## Required bullets\n${bulletList}`,
  ];
  if (variant.recipient) parts.push(`Recipient: ${variant.recipient}`);
  if (variant.greeting) parts.push(`Expected greeting: ${variant.greeting}`);
  if (variant.closing) parts.push(`Expected closing: ${variant.closing}`);
  if (variant.recipientRelationship) {
    parts.push(
      `Recipient relationship: ${variant.recipientRelationship} — apply the register rule for this relationship from the system prompt.`,
    );
  }
  if (variant.inventCount && variant.inventCount > 0) {
    parts.push(
      `Invent-X: the student must invent ${variant.inventCount} concrete item(s) of their own. Count and grade per the invent-X rule.`,
    );
  }
  if (variant.imageUrls.length > 0) {
    const captions = variant.imageUrls.map((u) => u.captionWord).join(", ");
    parts.push(
      `Image caption words: ${captions} — the student must mention each by name in the response.`,
    );
  }
  parts.push(`## Student answer\n${userText}`);
  return parts.join("\n\n");
}
